import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { familyAPI, categoriesAPI } from "../api";
import useAuthStore from "../store/auth";
import { formatAmount } from "../utils/currency";

const COLORS = ["#7c6af7","#f76ab0","#6af7b8","#f7c26a","#6ab8f7","#f76a6a"];
const ROLE_LABELS = { owner:"Proprietaire", admin:"Administrateur", member:"Membre" };
const ROLE_COLORS = { owner:"var(--accent)", admin:"var(--accent2)", member:"var(--text2)" };

const Modal = ({ title, onClose, children }) => (
  <div style={{ position:"fixed", inset:0, background:"#00000090", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
    <div style={{ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:20, padding:32, width:"100%", maxWidth:480 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700 }}>{title}</h3>
        <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, color:"var(--text2)", cursor:"pointer" }}>x</button>
      </div>
      {children}
    </div>
  </div>
);

export default function FamilyPage() {
  const { t } = useTranslation();
  const { hasAccess, user } = useAuthStore();
  const currency = user?.currency || "EUR";
  const fmt = n => formatAmount(n, currency);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [tab, setTab]                   = useState("overview");
  const [month, setMonth]               = useState(new Date().toISOString().slice(0, 7));
  const [groupName, setGroupName]       = useState("");
  const [editGroupName, setEditGroupName] = useState("");
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showBudgetModal, setShowBudgetModal]   = useState(false);
  const [editingExpense, setEditingExpense]     = useState(null);
  const [memberForm, setMemberForm] = useState({ name:"", email:"", role:"member" });
  const [expenseForm, setExpenseForm] = useState({ amount:"", category_id:"", date:new Date().toISOString().split("T")[0], description:"" });
  const [budgetForm, setBudgetForm]   = useState({ category_id:"", amount:"" });

  const { data: group, isLoading: gLoading } = useQuery({ queryKey:["family-group"],   queryFn:() => familyAPI.getGroup().then(r => r.data),   enabled:hasAccess() });
  const { data: members }  = useQuery({ queryKey:["family-members"],  queryFn:() => familyAPI.getMembers().then(r => r.data),  enabled:hasAccess() && !!group });
  const { data: stats }    = useQuery({ queryKey:["family-stats", month],   queryFn:() => familyAPI.getStats({ month }).then(r => r.data),    enabled:hasAccess() && !!group && ["overview","budget"].includes(tab) });
  const { data: fExpenses } = useQuery({ queryKey:["family-expenses", month], queryFn:() => familyAPI.getExpenses({ month }).then(r => r.data), enabled:hasAccess() && !!group && tab === "expenses" });
  const { data: fIncome }   = useQuery({ queryKey:["family-income", month],   queryFn:() => familyAPI.getIncome({ month }).then(r => r.data),   enabled:hasAccess() && !!group && tab === "income" });
  const { data: fBudgets }  = useQuery({ queryKey:["family-budgets", month],  queryFn:() => familyAPI.getBudgets({ month }).then(r => r.data),  enabled:hasAccess() && !!group && tab === "budget" });
  const { data: cats }      = useQuery({ queryKey:["categories"],              queryFn:() => categoriesAPI.list().then(r => r.data),             enabled:hasAccess() && !!group });

  const createGroup  = useMutation({ mutationFn: d => familyAPI.createGroup(d),      onSuccess: () => { qc.invalidateQueries(["family-group"]); toast.success("Groupe cree !"); } });
  const updateGroup  = useMutation({ mutationFn: d => familyAPI.updateGroup(d),      onSuccess: () => { qc.invalidateQueries(["family-group"]); setShowEditGroup(false); toast.success("Mis a jour"); } });
  const deleteGroup  = useMutation({ mutationFn: ()  => familyAPI.deleteGroup(),     onSuccess: () => { qc.invalidateQueries(["family-group","family-members"]); toast.success("Groupe supprime"); } });
  const addMember    = useMutation({ mutationFn: d => familyAPI.addMember(d),        onSuccess: () => { qc.invalidateQueries(["family-members","family-group"]); setShowAddMember(false); setMemberForm({ name:"", email:"", role:"member" }); toast.success(memberForm.email ? "Invitation envoyee !" : "Membre ajoute !"); } });
  const updateMember = useMutation({ mutationFn: ({ id, role }) => familyAPI.updateMember(id, { role }), onSuccess: () => { qc.invalidateQueries(["family-members"]); toast.success("Role mis a jour"); } });
  const removeMember = useMutation({ mutationFn: id => familyAPI.removeMember(id),   onSuccess: () => { qc.invalidateQueries(["family-members","family-group"]); toast.success("Membre retire"); } });
  const saveExpense  = useMutation({
    mutationFn: d => editingExpense ? familyAPI.updateExpense(editingExpense.id, d) : familyAPI.addExpense(d),
    onSuccess: () => { qc.invalidateQueries(["family-expenses","family-stats"]); setShowExpenseModal(false); setEditingExpense(null); toast.success("Depense enregistree"); }
  });
  const deleteExpense = useMutation({ mutationFn: id => familyAPI.deleteExpense(id), onSuccess: () => { qc.invalidateQueries(["family-expenses","family-stats"]); toast.success("Depense supprimee"); } });
  const saveBudget    = useMutation({ mutationFn: d => familyAPI.saveBudget({ ...d, month }), onSuccess: () => { qc.invalidateQueries(["family-budgets"]); setShowBudgetModal(false); toast.success("Budget enregistre"); } });

  const inp = { background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:10, padding:"11px 14px", color:"var(--text)", fontSize:14, outline:"none", boxSizing:"border-box", width:"100%" };
  const card = (extra) => ({ background:"var(--surface)", border:"1px solid var(--border)", borderRadius:16, padding:24, ...extra });

  const isOwner = group?.role === "owner";
  const isAdmin = ["owner","admin"].includes(group?.role);

  if (!hasAccess()) return (
    <div style={{ textAlign:"center", padding:"80px 20px" }}>
      <div style={{ fontSize:64, marginBottom:16 }}>⏰</div>
      <h2 style={{ fontFamily:"Syne,sans-serif", marginBottom:12 }}>{t("trial_expired")}</h2>
      <p style={{ color:"var(--text2)", marginBottom:24 }}>{t("upgrade_message")}</p>
      <button onClick={() => navigate("/pricing")} style={{ padding:"14px 32px", background:"var(--accent)", color:"#fff", border:"none", borderRadius:12, fontFamily:"Syne,sans-serif", fontWeight:700, fontSize:16, cursor:"pointer" }}>{t("upgrade")}</button>
    </div>
  );

  if (gLoading) return <div style={{ padding:40, color:"var(--text2)" }}>Chargement...</div>;

  if (!group) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:400 }}>
      <div style={{ ...card(), maxWidth:480, width:"100%", textAlign:"center" }}>
        <div style={{ fontSize:64, marginBottom:16 }}>👨‍👩‍👧</div>
        <h2 style={{ fontFamily:"Syne,sans-serif", fontSize:22, fontWeight:700, marginBottom:8 }}>Budget Familial</h2>
        <p style={{ color:"var(--text2)", fontSize:15, lineHeight:1.7, marginBottom:28 }}>Gerez vos finances en commun avec votre famille ou vos colocataires.</p>
        <div style={{ background:"var(--surface2)", borderRadius:12, padding:20, marginBottom:24, textAlign:"left" }}>
          {["Inviter les membres par email","Voir les depenses et revenus de chaque membre","Repartition equitable des charges","Gestion des budgets communs (admin)"].map(f => (
            <div key={f} style={{ display:"flex", gap:8, marginBottom:8, fontSize:14, color:"var(--text2)" }}><span style={{ color:"var(--accent3)" }}>✓</span>{f}</div>
          ))}
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="Nom du groupe..." style={{ ...inp, flex:1 }} />
          <button onClick={() => groupName.trim() && createGroup.mutate({ name:groupName.trim() })}
            style={{ padding:"11px 20px", background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
            Creer
          </button>
        </div>
      </div>
    </div>
  );

  const pendingMembers = (members || []).filter(m => !m.accepted);
  const activeMembers  = (members || []).filter(m => m.accepted);

  const TABS = [
    { key:"overview",  label:"Vue densemble", icon:"📊" },
    { key:"members",   label:"Membres",       icon:"👥" },
    { key:"expenses",  label:"Depenses",      icon:"💸" },
    { key:"income",    label:"Revenus",       icon:"📈" },
    { key:"budget",    label:"Budgets",       icon:"🎯" },
  ];

  const openAddExpense = () => { setEditingExpense(null); setExpenseForm({ amount:"", category_id:cats?.[0]?.id||"", date:new Date().toISOString().split("T")[0], description:"" }); setShowExpenseModal(true); };
  const openEditExpense = (e) => {
    const canEdit = isAdmin || e.user_id === user?.id;
    if (!canEdit) { toast.error("Vous ne pouvez modifier que vos propres depenses"); return; }
    setEditingExpense(e);
    setExpenseForm({ amount:e.amount, category_id:e.category_id, date:e.date?.split("T")[0], description:e.description||"" });
    setShowExpenseModal(true);
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
            <h1 style={{ fontFamily:"Syne,sans-serif", fontSize:24, fontWeight:700 }}>{group.name}</h1>
            {isOwner && <button onClick={() => { setEditGroupName(group.name); setShowEditGroup(true); }} style={{ background:"none", border:"none", color:"var(--text3)", fontSize:16, cursor:"pointer" }}>✏️</button>}
          </div>
          <p style={{ color:"var(--text2)", fontSize:14 }}>
            {(group.member_count||0)+1} membre(s) · Role : <strong style={{ color:ROLE_COLORS[group.role] }}>{ROLE_LABELS[group.role]}</strong>
            {pendingMembers.length > 0 && <span style={{ marginLeft:10, background:"#f7c26a20", color:"#f7a000", padding:"2px 8px", borderRadius:20, fontSize:12 }}>⏳ {pendingMembers.length} en attente</span>}
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {isAdmin && <button onClick={() => setShowAddMember(true)} style={{ padding:"10px 16px", background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:600, fontSize:14, cursor:"pointer" }}>+ Inviter</button>}
        </div>
      </div>

      <div style={{ display:"flex", gap:4, background:"var(--surface)", border:"1px solid var(--border)", borderRadius:12, padding:4, marginBottom:24, overflowX:"auto" }}>
        {TABS.map(t_ => (
          <button key={t_.key} onClick={() => setTab(t_.key)}
            style={{ padding:"9px 14px", borderRadius:8, border:"none", background:tab===t_.key?"var(--accent)":"transparent", color:tab===t_.key?"#fff":"var(--text2)", fontSize:13, fontWeight:500, cursor:"pointer", display:"flex", alignItems:"center", gap:5, whiteSpace:"nowrap" }}>
            {t_.icon} {t_.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Vue densemble ── */}
      {tab === "overview" && stats && (
        <div>
          <div style={{ marginBottom:16 }}>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ ...inp, width:"auto" }} />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:16, marginBottom:20 }}>
            {[
              { label:"Revenus",  value:fmt(stats.total_income),  color:"var(--accent3)", icon:"📈" },
              { label:"Depenses", value:fmt(stats.total_expense), color:"var(--danger)",  icon:"📉" },
              { label:"Solde",    value:fmt(stats.balance),       color:"var(--accent)",  icon:"💰" },
              { label:"Membres",  value:(stats.members?.length||1)+" pers.", color:"var(--accent2)", icon:"👥" },
            ].map(s => (
              <div key={s.label} style={{ ...card({ borderTop:"3px solid "+s.color }) }}>
                <div style={{ fontSize:20, marginBottom:8 }}>{s.icon}</div>
                <div style={{ fontSize:11, color:"var(--text2)", marginBottom:4, textTransform:"uppercase" }}>{s.label}</div>
                <div style={{ fontFamily:"Syne,sans-serif", fontSize:18, fontWeight:700, color:s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
          <div style={card()}>
            <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:16 }}>Contribution par membre</h3>
            {(stats.members||[]).map((m, i) => (
              <div key={m.user_id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 0", borderBottom:"1px solid var(--border)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <div style={{ width:36, height:36, borderRadius:"50%", background:COLORS[i%COLORS.length], display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:14 }}>
                    {m.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:14 }}>{m.name}</div>
                    <div style={{ fontSize:12, color:ROLE_COLORS[m.role] }}>{ROLE_LABELS[m.role]}</div>
                  </div>
                </div>
                <div style={{ textAlign:"right", fontSize:14 }}>
                  <div style={{ color:"var(--accent3)" }}>+{fmt(m.income)}</div>
                  <div style={{ color:"var(--danger)" }}>-{fmt(m.expense)}</div>
                  <div style={{ fontWeight:700, color:m.income-m.expense>=0?"var(--accent3)":"var(--danger)" }}>
                    {m.income-m.expense>=0?"+":""}{fmt(m.income-m.expense)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab: Membres ── */}
      {tab === "members" && (
        <div>
          {pendingMembers.length > 0 && (
            <div style={{ ...card({ marginBottom:20, borderColor:"#f7c26a50" }) }}>
              <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:12, color:"#f7a000" }}>⏳ Invitations en attente</h3>
              {pendingMembers.map(m => (
                <div key={m.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid var(--border)" }}>
                  <div>
                    <div style={{ fontWeight:600 }}>{m.name}</div>
                    <div style={{ fontSize:12, color:"var(--text2)" }}>{m.email} · {ROLE_LABELS[m.role]}</div>
                  </div>
                  {isAdmin && <button onClick={() => removeMember.mutate(m.id)} style={{ padding:"6px 12px", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:8, color:"var(--danger)", fontSize:12, cursor:"pointer" }}>Annuler</button>}
                </div>
              ))}
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:16 }}>
            <div style={{ ...card({ borderTop:"3px solid var(--accent)", textAlign:"center" }) }}>
              <div style={{ width:52, height:52, borderRadius:"50%", margin:"0 auto 12px", background:"linear-gradient(135deg,#7c6af7,#f7c26a)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:"#fff", fontWeight:700 }}>
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div style={{ fontWeight:700, fontSize:15, marginBottom:2 }}>{user?.name}</div>
              <div style={{ fontSize:12, color:"var(--text2)", marginBottom:10 }}>{user?.email}</div>
              <span style={{ padding:"3px 12px", background:"#7c6af720", color:"var(--accent)", borderRadius:20, fontSize:12, fontWeight:600 }}>Proprietaire</span>
            </div>
            {activeMembers.map((m, i) => (
              <div key={m.id} style={{ ...card({ textAlign:"center" }) }}>
                <div style={{ width:52, height:52, borderRadius:"50%", margin:"0 auto 12px", background:COLORS[i%COLORS.length], display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color:"#fff", fontWeight:700 }}>
                  {m.name?.[0]?.toUpperCase()}
                </div>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:2 }}>{m.name}</div>
                {m.email && <div style={{ fontSize:12, color:"var(--text2)", marginBottom:8 }}>{m.email}</div>}
                <span style={{ padding:"3px 12px", background:"var(--surface2)", color:ROLE_COLORS[m.role], borderRadius:20, fontSize:12 }}>{ROLE_LABELS[m.role]}</span>
                {isOwner && (
                  <div style={{ marginTop:10, display:"flex", gap:6 }}>
                    <select value={m.role} onChange={e => updateMember.mutate({ id:m.id, role:e.target.value })} style={{ ...inp, padding:"6px 8px", fontSize:12, flex:1 }}>
                      <option value="member">Membre</option>
                      <option value="admin">Admin</option>
                    </select>
                    <button onClick={() => window.confirm(t("confirm_delete")) && removeMember.mutate(m.id)} style={{ padding:"6px 10px", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:8, color:"var(--danger)", fontSize:12, cursor:"pointer" }}>x</button>
                  </div>
                )}
              </div>
            ))}
            {isAdmin && (
              <div style={{ ...card({ border:"2px dashed var(--border)", background:"transparent", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:160, cursor:"pointer" }) }} onClick={() => setShowAddMember(true)}>
                <div style={{ fontSize:32, opacity:0.4, marginBottom:8 }}>+</div>
                <div style={{ color:"var(--text3)", fontSize:13 }}>Inviter un membre</div>
              </div>
            )}
          </div>
          {isOwner && (
            <div style={{ marginTop:28, padding:18, border:"1px solid var(--danger)", borderRadius:12, background:"#f76a6a08" }}>
              <div style={{ fontWeight:700, color:"var(--danger)", marginBottom:6 }}>Zone dangereuse</div>
              <p style={{ fontSize:13, color:"var(--text2)", marginBottom:10 }}>Supprimer le groupe retire tous les membres.</p>
              <button onClick={() => window.confirm("Confirmer la suppression du groupe ?") && deleteGroup.mutate()} style={{ padding:"8px 16px", background:"var(--danger)", color:"#fff", border:"none", borderRadius:8, fontSize:13, cursor:"pointer" }}>Supprimer le groupe</button>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Depenses ── */}
      {tab === "expenses" && (
        <div>
          <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ ...inp, width:"auto" }} />
            <button onClick={openAddExpense} style={{ padding:"10px 16px", background:"var(--danger)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:600, fontSize:14, cursor:"pointer" }}>+ Ma depense</button>
          </div>
          <div style={card()}>
            <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:16 }}>
              Depenses du groupe — {month}
              {isAdmin && <span style={{ marginLeft:8, fontSize:13, color:"var(--text2)", fontWeight:400 }}>(Admin : vous pouvez modifier/supprimer toutes les depenses)</span>}
            </h3>
            {!(fExpenses?.length) ? (
              <p style={{ color:"var(--text3)", textAlign:"center", padding:40 }}>Aucune depense ce mois</p>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>{["Date","Membre","Categorie","Description","Montant","Actions"].map(h => (
                      <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", color:"var(--text3)", borderBottom:"1px solid var(--border)" }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {(fExpenses||[]).map(e => {
                      const canEdit = isAdmin || e.user_id === user?.id;
                      return (
                        <tr key={e.id} style={{ borderBottom:"1px solid var(--border)" }}>
                          <td style={{ padding:10, color:"var(--text2)", fontSize:13 }}>{new Date(e.date).toLocaleDateString("fr-FR")}</td>
                          <td style={{ padding:10, fontSize:13 }}>
                            <span style={{ padding:"3px 8px", borderRadius:20, fontSize:11, background: e.user_id === user?.id ? "#7c6af720":"var(--surface2)", color:e.user_id===user?.id?"var(--accent)":"var(--text2)" }}>
                              {e.member_name || "-"}
                            </span>
                          </td>
                          <td style={{ padding:10 }}><span style={{ padding:"3px 8px", borderRadius:20, fontSize:11, background:(e.category_color||"#7c6af7")+"20", color:e.category_color||"var(--accent)" }}>{e.category_icon} {e.category_name}</span></td>
                          <td style={{ padding:10, fontSize:13, color:"var(--text2)" }}>{e.description||"-"}</td>
                          <td style={{ padding:10, fontFamily:"Syne,sans-serif", fontWeight:700, color:"var(--danger)" }}>-{fmt(e.amount)}</td>
                          <td style={{ padding:10 }}>
                            {canEdit && (
                              <div style={{ display:"flex", gap:4 }}>
                                <button onClick={() => openEditExpense(e)} style={{ padding:"4px 8px", background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:6, fontSize:11, cursor:"pointer", color:"var(--text)" }}>Modifier</button>
                                <button onClick={() => window.confirm(t("confirm_delete")) && deleteExpense.mutate(e.id)} style={{ padding:"4px 8px", background:"var(--danger)", border:"none", borderRadius:6, fontSize:11, cursor:"pointer", color:"#fff" }}>Supp.</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Revenus ── */}
      {tab === "income" && (
        <div>
          <div style={{ marginBottom:16 }}>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ ...inp, width:"auto" }} />
          </div>
          <div style={card()}>
            <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:16 }}>Revenus du groupe — {month}</h3>
            {!(fIncome?.length) ? (
              <p style={{ color:"var(--text3)", textAlign:"center", padding:40 }}>Aucun revenu ce mois</p>
            ) : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>{["Date","Membre","Source","Description","Montant"].map(h => (
                      <th key={h} style={{ textAlign:"left", padding:"10px 12px", fontSize:11, fontWeight:700, textTransform:"uppercase", color:"var(--text3)", borderBottom:"1px solid var(--border)" }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {(fIncome||[]).map(i => (
                      <tr key={i.id} style={{ borderBottom:"1px solid var(--border)" }}>
                        <td style={{ padding:10, color:"var(--text2)", fontSize:13 }}>{new Date(i.date).toLocaleDateString("fr-FR")}</td>
                        <td style={{ padding:10 }}>
                          <span style={{ padding:"3px 8px", borderRadius:20, fontSize:11, background:i.user_id===user?.id?"#7c6af720":"var(--surface2)", color:i.user_id===user?.id?"var(--accent)":"var(--text2)" }}>
                            {i.member_name||"-"}
                          </span>
                        </td>
                        <td style={{ padding:10, fontSize:13 }}><span style={{ padding:"3px 8px", borderRadius:20, fontSize:11, background:"#6af7b820", color:"var(--accent3)" }}>{i.source}</span></td>
                        <td style={{ padding:10, fontSize:13, color:"var(--text2)" }}>{i.description||"-"}</td>
                        <td style={{ padding:10, fontFamily:"Syne,sans-serif", fontWeight:700, color:"var(--accent3)" }}>+{fmt(i.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Budgets ── */}
      {tab === "budget" && (
        <div>
          <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ ...inp, width:"auto" }} />
            {isAdmin && (
              <button onClick={() => { setBudgetForm({ category_id:cats?.[0]?.id||"", amount:"" }); setShowBudgetModal(true); }}
                style={{ padding:"10px 16px", background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:600, fontSize:14, cursor:"pointer" }}>
                + Budget
              </button>
            )}
            {!isAdmin && <span style={{ fontSize:13, color:"var(--text2)" }}>Seuls les admins peuvent creer des budgets communs.</span>}
          </div>
          {stats && (
            <div style={{ ...card({ marginBottom:20 }) }}>
              <h3 style={{ fontFamily:"Syne,sans-serif", fontWeight:700, marginBottom:16 }}>Repartition des charges</h3>
              <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:"1px solid var(--border)", fontSize:14 }}>
                <span style={{ color:"var(--text2)" }}>Total depenses groupe</span>
                <span style={{ fontWeight:700 }}>{fmt(stats.total_expense)}</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:"1px solid var(--border)", fontSize:14 }}>
                <span style={{ color:"var(--text2)" }}>Nombre de membres</span>
                <span style={{ fontWeight:700 }}>{stats.members?.length||1} pers.</span>
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0", fontSize:15 }}>
                <span style={{ fontWeight:600 }}>Part par personne</span>
                <span style={{ fontFamily:"Syne,sans-serif", fontWeight:800, color:"var(--accent)", fontSize:18 }}>{fmt((stats.total_expense||0)/(stats.members?.length||1))}</span>
              </div>
            </div>
          )}
          {!(fBudgets?.length) ? (
            <div style={{ ...card({ textAlign:"center" }) }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🎯</div>
              <p style={{ color:"var(--text3)" }}>{isAdmin ? "Aucun budget commun. Creez-en un !" : "Aucun budget commun defini."}</p>
            </div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:16 }}>
              {(fBudgets||[]).map(b => {
                const pct  = b.amount > 0 ? Math.min(b.spent/b.amount*100, 100) : 0;
                const over = parseFloat(b.spent) > parseFloat(b.amount);
                const color = over ? "var(--danger)" : pct > 80 ? "#f7c26a" : "var(--accent3)";
                return (
                  <div key={b.id} style={{ ...card({ borderTop:"3px solid "+(over?"var(--danger)":"var(--border)") }) }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:20 }}>{b.category_icon}</span>
                        <div>
                          <div style={{ fontWeight:600, fontSize:14 }}>{b.category_name}</div>
                          <div style={{ fontSize:11, color:"var(--text2)" }}>Budget commun</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:13 }}>
                      <span style={{ color:"var(--text2)" }}>Depense : <strong style={{ color }}>{fmt(b.spent)}</strong></span>
                      <span style={{ color:"var(--text2)" }}>{fmt(b.amount)}</span>
                    </div>
                    <div style={{ height:8, background:"var(--surface2)", borderRadius:4, overflow:"hidden", marginBottom:6 }}>
                      <div style={{ height:"100%", width:pct+"%", background:color, borderRadius:4 }} />
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--text2)" }}>
                      <span>{pct.toFixed(0)}%</span>
                      <span style={{ color, fontWeight:600 }}>{over ? "Depasse de "+fmt(b.spent-b.amount) : "Reste "+fmt(b.amount-b.spent)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Modal: Inviter membre ── */}
      {showAddMember && (
        <Modal title="Inviter un membre" onClose={() => setShowAddMember(false)}>
          <div style={{ background:"var(--surface2)", borderRadius:10, padding:12, marginBottom:16, fontSize:13, color:"var(--text2)" }}>
            Avec email : un lien d invitation sera envoye. Sans email : membre ajoute directement.
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>Nom *</label>
            <input value={memberForm.name} onChange={e => setMemberForm({...memberForm, name:e.target.value})} placeholder="Prenom Nom" style={inp} />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>Email (pour lien invitation)</label>
            <input value={memberForm.email} onChange={e => setMemberForm({...memberForm, email:e.target.value})} placeholder="email@exemple.com" style={inp} />
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>Role</label>
            <select value={memberForm.role} onChange={e => setMemberForm({...memberForm, role:e.target.value})} style={inp}>
              <option value="member">Membre — voir depenses, revenus, ajouter ses depenses</option>
              <option value="admin">Administrateur — aussi : inviter, modifier toutes depenses, budgets</option>
            </select>
          </div>
          <div style={{ display:"flex", gap:12 }}>
            <button onClick={() => setShowAddMember(false)} style={{ flex:1, padding:12, background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:10, cursor:"pointer", color:"var(--text)" }}>{t("cancel")}</button>
            <button onClick={() => memberForm.name.trim() && addMember.mutate(memberForm)}
              style={{ flex:1, padding:12, background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer" }}>
              {memberForm.email ? "Envoyer invitation" : "Ajouter"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Modal: Depense ── */}
      {showExpenseModal && (
        <Modal title={editingExpense ? "Modifier la depense" : "Ajouter une depense"} onClose={() => { setShowExpenseModal(false); setEditingExpense(null); }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
            <div>
              <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("amount")}</label>
              <input type="number" value={expenseForm.amount} onChange={e => setExpenseForm({...expenseForm, amount:e.target.value})} style={inp} />
            </div>
            <div>
              <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("category")}</label>
              <select value={expenseForm.category_id} onChange={e => setExpenseForm({...expenseForm, category_id:e.target.value})} style={inp}>
                {(cats||[]).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("date")}</label>
            <input type="date" value={expenseForm.date} onChange={e => setExpenseForm({...expenseForm, date:e.target.value})} style={inp} />
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("description")}</label>
            <textarea value={expenseForm.description} onChange={e => setExpenseForm({...expenseForm, description:e.target.value})} style={{ ...inp, minHeight:70, resize:"vertical" }} />
          </div>
          <div style={{ display:"flex", gap:12 }}>
            <button onClick={() => { setShowExpenseModal(false); setEditingExpense(null); }} style={{ flex:1, padding:12, background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:10, cursor:"pointer", color:"var(--text)" }}>{t("cancel")}</button>
            <button onClick={() => expenseForm.amount && expenseForm.category_id && saveExpense.mutate(expenseForm)}
              style={{ flex:1, padding:12, background:"var(--danger)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer" }}>{t("save")}</button>
          </div>
        </Modal>
      )}

      {/* ── Modal: Budget commun ── */}
      {showBudgetModal && (
        <Modal title="Ajouter un budget commun" onClose={() => setShowBudgetModal(false)}>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("category")}</label>
            <select value={budgetForm.category_id} onChange={e => setBudgetForm({...budgetForm, category_id:e.target.value})} style={inp}>
              {(cats||[]).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:20 }}>
            <label style={{ display:"block", fontSize:13, color:"var(--text2)", marginBottom:8 }}>{t("amount")}</label>
            <input type="number" value={budgetForm.amount} onChange={e => setBudgetForm({...budgetForm, amount:e.target.value})} style={inp} placeholder="0" />
          </div>
          <div style={{ display:"flex", gap:12 }}>
            <button onClick={() => setShowBudgetModal(false)} style={{ flex:1, padding:12, background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:10, cursor:"pointer", color:"var(--text)" }}>{t("cancel")}</button>
            <button onClick={() => budgetForm.category_id && budgetForm.amount && saveBudget.mutate(budgetForm)}
              style={{ flex:1, padding:12, background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer" }}>{t("save")}</button>
          </div>
        </Modal>
      )}

      {/* ── Modal: Renommer groupe ── */}
      {showEditGroup && (
        <Modal title="Renommer le groupe" onClose={() => setShowEditGroup(false)}>
          <input value={editGroupName} onChange={e => setEditGroupName(e.target.value)} style={{ ...inp, marginBottom:20 }} />
          <div style={{ display:"flex", gap:12 }}>
            <button onClick={() => setShowEditGroup(false)} style={{ flex:1, padding:12, background:"var(--surface2)", border:"1px solid var(--border)", borderRadius:10, cursor:"pointer", color:"var(--text)" }}>{t("cancel")}</button>
            <button onClick={() => editGroupName.trim() && updateGroup.mutate({ name:editGroupName.trim() })}
              style={{ flex:1, padding:12, background:"var(--accent)", color:"#fff", border:"none", borderRadius:10, fontFamily:"Syne,sans-serif", fontWeight:700, cursor:"pointer" }}>{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}